<template>
  <div>
    <div class="input-group mb-3">
      <input v-model="searchQuery" class="form-control" type="text" placeholder="Buscar..." />
      <button @click="searchData(1)" class="btn btn-primary" type="button">Buscar</button>
    </div>

    <!-- Mostrar spinner mientras se cargan los datos -->
    <div v-if="isLoading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2">Espere...cargando proyectos...</p>
    </div>

    <!-- Tabla de datos -->
    <div v-else>
      <table class="table table-striped custom-table">
        <thead class="custom-thead">
          <tr>
            <th @click="sort('rsocial')" style="cursor: pointer;">
              Razón Social
              <span v-if="sortColumn === 'rsocial'">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sort('cif')" style="cursor: pointer;">
              CIF
              <span v-if="sortColumn === 'cif'">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sort('comercial')" style="cursor: pointer;">
              Comercial
              <span v-if="sortColumn === 'comercial'">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sort('colaborador')" style="cursor: pointer;">
              Colaborador Externo
              <span v-if="sortColumn === 'colaborador'">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sort('fecha')" style="cursor: pointer;">
              Fecha de creación
              <span v-if="sortColumn === 'fecha'">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sort('vencimiento')" style="cursor: pointer;">
              Próximo vencimiento
              <span v-if="sortColumn === 'vencimiento'">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th>Acciones</th> <!-- Nueva columna para acciones -->
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in items" :key="index">
            <td><b>{{ item.rsocial }}</b> <span class="badge" :class="{
              'bg-danger': item.numero_columnas === 0,      // Rojo si el número es 0
              'bg-warning': item.numero_columnas > 0 && item.numero_columnas < 3,  // Amarillo si es menor que 3 pero mayor que 0
              'bg-primary': item.numero_columnas >= 3       // Azul para 3 o más
            }">{{ item.numero_columnas }}</span></td>
            <td>{{ item.cif }}</td>
            <td>{{ item.comercial_nombre }}</td>
            <td>{{ item.colaborador }}</td>
            <td>{{ item.fecha }}</td>
            <td :class="getVencimientoClass(item.vencimiento)">
              {{ item.vencimiento }}
            </td>
            <td>
              <!-- Botón para ver en PDF -->
              <a :href="`${djangoUrl}/cuadrocomparativopdf/${item.id}/`" target="_blank"
                class="btn btn-outline-primary btn-sm">
                <i class="fas fa-file-pdf"></i> Pdf
              </a>
              &nbsp;
              <!-- Botón para editar -->
              <a :href="`${djangoUrl}/proyectocomunidad/editar/${item.id}/`" target="_blank"
                class="btn btn-outline-secondary btn-sm">
                <i class="fas fa-edit"></i> Editar
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación -->
      <nav aria-label="Page navigation">
        <ul class="pagination justify-content-center custom-pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Anterior</a>
          </li>

          <!-- Mostrar la primera página y puntos suspensivos si es necesario -->
          <li v-if="currentPage > 3" class="page-item" @click="changePage(1)">
            <a class="page-link" href="#">1</a>
          </li>
          <li v-if="currentPage > 3" class="page-item disabled">
            <a class="page-link" href="#">...</a>
          </li>

          <!-- Iterar sobre las páginas visibles -->
          <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
            <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
          </li>

          <!-- Mostrar puntos suspensivos y la última página si es necesario -->
          <li v-if="currentPage < totalPages - 2" class="page-item disabled">
            <a class="page-link" href="#">...</a>
          </li>
          <li v-if="currentPage < totalPages - 1" class="page-item" @click="changePage(totalPages)">
            <a class="page-link" href="#">{{ totalPages }}</a>
          </li>

          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Siguiente</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from "axios"; // Importamos axios

export default {
  data() {
    return {
      searchQuery: "", // Almacenar la cadena de búsqueda
      sortColumn: "fecha",
      sortOrder: "desc",
      currentPage: 1,
      itemsPerPage: 10,
      items: [],
      isLoading: false,
      djangoUrl: "",
      totalPages: 0,
    };
  },
  computed: {
    filteredData() {
      // Verificar si this.items es un array antes de usar filter
      if (Array.isArray(this.items)) {
        const searchLower = this.search.toLowerCase();

        // Filtrar los elementos del array
        return this.items.filter((item) => {
          return (
            item.rsocial.toLowerCase().includes(searchLower) ||  // Buscar en R.Social
            item.cif.toLowerCase().includes(searchLower) ||      // Buscar en CIF
            item.comercial.toLowerCase().includes(searchLower) ||  // Buscar en Comercial
            item.colaborador.toLowerCase().includes(searchLower) ||  // Buscar en Colaborador
            item.fecha.toLowerCase().includes(searchLower)      // Buscar en Fecha
          );
        });
      } else {
        // Si no es un array, mostrar un error y devolver un array vacío
        console.error("this.items no es un array");
        return [];
      }
    },
    visiblePages() {
      const pages = [];
      const range = 2; // Número de páginas a mostrar a cada lado de la página actual
      let startPage = Math.max(this.currentPage - range, 1);
      let endPage = Math.min(this.currentPage + range, this.totalPages);

      // Añadir las páginas dentro del rango
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    },
    sortedData() {
      if (!this.sortColumn) return this.filteredData;
      return this.filteredData.sort((a, b) => {
        const modifier = this.sortOrder === "asc" ? 1 : -1;
        if (a[this.sortColumn] < b[this.sortColumn]) return -1 * modifier;
        if (a[this.sortColumn] > b[this.sortColumn]) return 1 * modifier;
        return 0;
      });
    },
  },
  methods: {
    getVencimientoClass(vencimiento) {
      const hoy = new Date(); // Obtener la fecha actual
      const fechaVencimiento = new Date(vencimiento); // Convertir la fecha de vencimiento a un objeto Date

      const diffTime = fechaVencimiento - hoy; // Diferencia en milisegundos
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convertir a días

      if (diffDays < 0) {
        // Fecha anterior a hoy
        return 'text-muted'; // Gris atenuado
      } else if (diffDays <= 30) {
        // Quedan menos de 30 días
        return 'text-danger'; // Rojo
      } else {
        // Más de 30 días restantes
        return 'text-success'; // Verde
      }
    },
    viewPdf(item) {
      // Lógica para ver el PDF del item
      console.log("Ver PDF de:", item);
    },
    editItem(item) {
      // Suponiendo que 'item.id' es el pk del proyecto comunidad

      const API_URL = import.meta.env.VITE_DJANGO_URL; // Usa la variable de entorno para la URL base
      const endpoint = `proyectocomunidad/editar/${item.id}/`;
      const fullUrl = `${API_URL}/${endpoint}`;
      console.log(fullUrl)
      // Abre una nueva ventana con la URL generada
      window.open(fullUrl, '_blank');
    },
    sort(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortOrder = "asc";
      }
      // Reiniciar la paginación a la primera página y forzar refresco de la tabla
      this.currentPage = 1;
      this.$forceUpdate(); // Forzar actualización de la tabla
    },
    async fetchData(page = 1) {
      this.isLoading = true;
      try {
        const API_URL = this.djangoUrl || import.meta.env.VITE_DJANGO_URL;
        const endpoint = `/api/proyectos-comunidad/?page=${page}&page_size=${this.itemsPerPage}`;
        const response = await axios.get(`${API_URL}${endpoint}`);
        if (response.data && Array.isArray(response.data.results)) {
          this.items = response.data.results;
          this.totalPages = Math.ceil(response.data.count / this.itemsPerPage);
          this.currentPage = page;
        } else {
          console.error("Los datos de la API no contienen un array en 'results'", response.data);
          this.items = [];
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        this.items = [];
      } finally {
        this.isLoading = false;
      }
    },
    async searchData(page = 1) {
      this.isLoading = true;
      try {
        const API_URL = this.djangoUrl || import.meta.env.VITE_DJANGO_URL;
        const endpoint = `/api/proyectos-comunidad/search/?q=${this.searchQuery}&page=${page}&page_size=${this.itemsPerPage}`;
        const response = await axios.get(`${API_URL}${endpoint}`);
        if (response.data && Array.isArray(response.data.results)) {
          this.items = response.data.results;
          this.totalPages = Math.ceil(response.data.count / this.itemsPerPage);
          this.currentPage = page;
        } else {
          console.error("Los datos de la API no contienen un array en 'results'", response.data);
          this.items = [];
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        this.items = [];
      } finally {
        this.isLoading = false;
      }
    },
    changePage(page) {
      if (typeof page === 'number' && page > 0 && page <= this.totalPages) {
        if (this.searchQuery) {
          this.searchData(page);  // Llama a `searchData` con el número de página
        } else {
          this.fetchData(page);  // Llama a `fetchData` con el número de página
        }
      }
    },

  },
  mounted() {
    console.log("cargando datos...");
    const urlParams = new URLSearchParams(window.location.search);
    this.djangoUrl = urlParams.get('backend_url') || import.meta.env.VITE_DJANGO_URL;
    this.fetchData();
  },
};
</script>

<style scoped>
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Personalización de la tabla */
.custom-table {
  font-size: 12px;
  /* Letra más pequeña */
}

.custom-thead {
  background-color: #343a40;
  /* Encabezado negro */
  color: #ffffff;
  /* Texto blanco */
  font-weight: bold;
  text-align: left;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.custom-thead th {
  padding: 12px;
  cursor: pointer;
  border-bottom: 2px solid #dee2e6;
  background-color: #343a40;
  /* Asegurar que el fondo siempre sea negro */
  color: #ffffff;
  /* Asegurar que las letras siempre sean blancas */
}

.custom-thead th:hover {
  background-color: #495057;
  /* Color de hover en el encabezado */
  color: #ffffff;
}

/* Estilos para las filas */
.table tbody tr:hover {
  background-color: #f1f3f5;
  /* Color de hover en las filas */
}

.table td {
  padding: 8px;
}

/* Botones de acción */
.btn-outline-primary {
  border-color: #007bff;
  color: #007bff;
}

.btn-outline-primary:hover {
  background-color: #007bff;
  color: #fff;
}

.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: #fff;
}

/* Estilos personalizados para la paginación */
.custom-pagination .page-link {
  color: #343a40;
  background-color: #ffffff;
  border: 1px solid #dee2e6;
}

.custom-pagination .page-item.active .page-link {
  background-color: #343a40;
  border-color: #343a40;
  color: #ffffff;
}

.custom-pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #ffffff;
  border-color: #dee2e6;
}

.custom-pagination .page-item:hover .page-link {
  background-color: #495057;
  border-color: #495057;
  color: #ffffff;
}

.text-muted {
  color: #6c757d;
}

.text-danger {
  color: #dc3545;
}

.text-success {
  color: #28a745;
}
</style>